import { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories, getVideosByCategory, videos } from '@/mocks/videos';
import type { Video } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeSlug, setActiveSlug] = useState(slug || 'dianshiju');
  const [displayedVideos, setDisplayedVideos] = useState<Video[]>([]);
  const [categoryName, setCategoryName] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    const vids = getVideosByCategory(activeSlug);
    setDisplayedVideos(vids.length > 0 ? vids : videos.filter((v) => v.category === activeSlug));
    const cat = categories.find((c) => c.slug === activeSlug);
    setCategoryName(cat?.name || '全部');
  }, [activeSlug]);

  // Breadcrumb for schema
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${categoryName}`, href: `/category/${activeSlug}` },
  ];

  // JSON-LD
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 蓝莓视频`,
    description: `蓝莓视频${categoryName}频道，提供海量${categoryName}资源，高清免费在线观看，全集不缓冲流畅体验。`,
    url: `https://www.blueberryvideo.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '蓝莓视频',
      url: 'https://www.blueberryvideo.com',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Page Header */}
            <div className="py-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                蓝莓视频{categoryName}频道，海量{categoryName}资源，高清免费在线观看，全集不缓冲流畅体验。
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-3 flex-wrap mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => {
                    setActiveSlug(cat.slug);
                    window.scrollTo({ top: 0, behavior: 'instant' });
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-foreground-950'
                      : 'bg-background-200 text-foreground-500 hover:text-foreground-900 hover:bg-background-300'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Video Grid */}
            {displayedVideos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 pb-12" data-product-shop="">
                {displayedVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mb-4">
                  <i className="ri-video-off-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无该分类的视频内容</p>
                <Link
                  to="/"
                  className="mt-4 px-4 py-2 rounded-lg bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}